import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import AnimatedSection from "./AnimatedSection";

const QuoteSection = () => {
  const { data: settings } = useQuery({
    queryKey: ["quote-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("setting_key, setting_value")
        .in("setting_key", ["quote_text", "quote_game_tagline", "quote_background_image", "quote_link_url", "quote_enabled"]);

      if (error) throw error;

      const settingsMap: Record<string, string> = {};
      data?.forEach((s) => {
        settingsMap[s.setting_key] = s.setting_value || "";
      });
      return settingsMap;
    },
  });

  const quoteText = settings?.quote_text;
  const gameTagline = settings?.quote_game_tagline;
  const backgroundImage = settings?.quote_background_image;
  const linkUrl = settings?.quote_link_url;

  // Don't render if disabled or no quote text
  if (settings?.quote_enabled === "false") return null;
  if (!quoteText) return null;

  return (
    <section className="relative overflow-hidden min-h-[500px] md:min-h-[600px]">
      {/* Background Image */}
      {backgroundImage ? (
        <>
          <img src={backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark/60 via-dark/70 to-dark/95" />
        </>
      ) : (
        <div className="absolute inset-0 bg-dark" />
      )}

      {/* Background accents */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-blood-red/5 rounded-full blur-[150px]" />
      </div>

      <div className="container mx-auto px-6 md:px-12 py-16 md:py-24 lg:py-32 relative z-10 h-full flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full">
          {/* Left Half - Empty (background shows through) */}
          <div className="hidden lg:block" />

          {/* Right Half - Quote Content */}
          <AnimatedSection animation="slide-right" className="lg:pl-8">
            {/* Large decorative quote mark */}
            <span className="block text-[120px] md:text-[180px] leading-none text-blood-red/40 font-serif -mb-16 md:-mb-24 select-none">
              "
            </span>

            {/* Quote text */}
            <blockquote className="relative mt-4">
              <p className="text-2xl md:text-3xl lg:text-4xl font-display text-dark-foreground leading-relaxed font-medium italic">
                {quoteText}
              </p>
            </blockquote>

            {/* Game tagline */}
            {gameTagline && (
              <div className="mt-8 flex items-center gap-4">
                <div className="w-12 h-px bg-blood-red" />
                {linkUrl ? (
                  linkUrl.startsWith("http") ? (
                    <a
                      href={linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blood-red uppercase tracking-[0.2em] text-sm font-semibold hover:text-dark-foreground transition-colors duration-300"
                    >
                      {gameTagline}
                    </a>
                  ) : (
                    <Link
                      to={linkUrl}
                      className="text-blood-red uppercase tracking-[0.2em] text-sm font-semibold hover:text-dark-foreground transition-colors duration-300"
                    >
                      {gameTagline}
                    </Link>
                  )
                ) : (
                  <span className="text-blood-red uppercase tracking-[0.2em] text-sm font-semibold">{gameTagline}</span>
                )}
              </div>
            )}
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default QuoteSection;
